import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listCustomerDocuments,
  getCustomerDocumentUrl,
} from "@/lib/customer-documents.functions";
import {
  FolderOpen,
  FileText,
  Image as ImageIcon,
  Package,
  Download,
  Loader2,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/account/documenten")({
  head: () => ({
    meta: [
      { title: "Documenten — Elvion Group Nederland" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DocumentsPage,
});

const CATEGORY_META: Record<
  string,
  { label: string; icon: typeof FileText; color: string }
> = {
  situation_photo: { label: "Situatiefoto", icon: ImageIcon, color: "text-emerald-600" },
  invoice: { label: "Factuur", icon: FileText, color: "text-blue-600" },
  werkbon: { label: "Werkbon", icon: FileText, color: "text-amber-600" },
  paklijst: { label: "Paklijst", icon: Package, color: "text-violet-600" },
  bundle: { label: "Dossier (bundel)", icon: FolderOpen, color: "text-[#0b2a4a]" },
  other: { label: "Overig", icon: FileText, color: "text-text-secondary" },
};

function DocumentsPage() {
  const listFn = useServerFn(listCustomerDocuments);
  const urlFn = useServerFn(getCustomerDocumentUrl);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["customer-documents", "self"],
    queryFn: () => listFn({ data: {} }),
  });

  const openMut = useMutation({
    mutationFn: async (id: string) => urlFn({ data: { id } }),
    onSuccess: (res) => {
      if (res?.url) window.open(res.url, "_blank", "noopener,noreferrer");
    },
  });

  const documents = q.data?.documents ?? [];
  const grouped = documents.reduce<Record<string, typeof documents>>((acc, doc: any) => {
    (acc[doc.category] ??= []).push(doc);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="font-display text-2xl font-bold text-[#0b2a4a]">Documenten</h1>
        <p className="text-sm text-text-secondary">
          Bekijk uw facturen, werkbonnen, paklijsten, situatiefoto's en volledige dossiers.
          Alle bestanden worden veilig bij uw account bewaard.
        </p>
      </header>

      {q.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Loader2 className="size-4 animate-spin" /> Documenten laden…
        </div>
      ) : q.isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Kon documenten niet laden. Probeer het later opnieuw.
        </div>
      ) : documents.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <FolderOpen className="mx-auto size-10 text-text-secondary/60" />
          <p className="mt-3 text-sm font-medium">Nog geen documenten</p>
          <p className="mt-1 text-xs text-text-secondary">
            Zodra wij een factuur, werkbon of situatiefoto voor u toevoegen verschijnt die hier.
          </p>
        </div>
      ) : (
        Object.entries(grouped).map(([category, docs]) => {
          const meta = CATEGORY_META[category] ?? CATEGORY_META.other;
          const Icon = meta.icon;
          return (
            <section
              key={category}
              className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
            >
              <header className="flex items-center gap-2 border-b border-border bg-muted/40 px-5 py-3">
                <Icon className={`size-4 ${meta.color}`} />
                <h2 className="font-display text-sm font-bold">{meta.label}</h2>
                <span className="text-xs text-text-secondary">
                  · {docs.length} bestand{docs.length === 1 ? "" : "en"}
                </span>
              </header>
              <ul className="divide-y divide-border">
                {docs.map((doc: any) => (
                  <li
                    key={doc.id}
                    className="flex items-center gap-4 px-5 py-3.5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{doc.title}</p>
                      <p className="mt-0.5 text-xs text-text-secondary">
                        {new Date(doc.created_at).toLocaleDateString("nl-NL", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                        {doc.size_bytes
                          ? ` · ${Math.round(doc.size_bytes / 1024)} kB`
                          : ""}
                        {doc.mime_type ? ` · ${doc.mime_type}` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openMut.mutate(doc.id)}
                      disabled={openMut.isPending}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-[#0b2a4a] transition hover:bg-muted/40 disabled:opacity-60"
                    >
                      {openMut.isPending && openMut.variables === doc.id ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Download className="size-3.5" />
                      )}
                      Open
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
